import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, Gift, Copy, Check, Share2, Users, Wallet, Sparkles,
  CheckCircle2, Clock, XCircle, Link2, TrendingUp,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { doc, setDoc } from 'firebase/firestore';
import {
  generateStandReferralCode, buildReferralLink,
  REFERRAL_CREDIT_CAP, REFERRAL_CREDIT_PER_FILLEUL, REFERRAL_WELCOME_DISCOUNT,
} from '../lib/referral';
import { toast } from 'react-hot-toast';

interface Filleul {
  applicationId: string;
  standName: string;
  status: string;
  applicantId: string;
  paidAt?: string;
}

interface ReferralStats {
  standReferralCode: string | null;
  festinCredit: number;
  totalFilleuls: number;
  confirmedFilleuls: number;
  filleuls: Filleul[];
}

const STATUS_META: Record<string, { label: string; badge: string; icon: typeof CheckCircle2 }> = {
  confirme: { label: 'Confirmé — crédité', badge: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', icon: CheckCircle2 },
  approuve_paiement_requis: { label: 'Paiement en attente', badge: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: Clock },
  pending: { label: 'En attente', badge: 'bg-white/5 text-gray-400 border-white/10', icon: Clock },
  rejected: { label: 'Refusé', badge: 'bg-red-500/10 text-red-400 border-red-500/20', icon: XCircle },
};

export default function StandReferralPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [fetching, setFetching] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate('/');
  }, [user, loading, navigate]);

  const loadStats = useCallback(async () => {
    if (!user) return;
    setFetching(true);
    try {
      const token = await user.getIdToken();
      const resp = await fetch(
        `${import.meta.env.VITE_API_URL || ''}/api/stands/${user.uid}/referral-stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error('fetch failed');
      let data: ReferralStats = await resp.json();

      // Backfill : les comptes créés avant la Phase 2 n'ont pas de code.
      // On en génère un côté client et on l'écrit sur le doc user (le user en
      // est propriétaire → autorisé par les règles Firestore).
      if (!data.standReferralCode) {
        const code = generateStandReferralCode();
        await setDoc(doc(db, 'users', user.uid), { standReferralCode: code, festinCredit: data.festinCredit || 0 }, { merge: true });
        data = { ...data, standReferralCode: code };
      }
      setStats(data);
    } catch (err) {
      console.error('referral-stats error', err);
      toast.error('Impossible de charger vos données de parrainage.');
    } finally {
      setFetching(false);
    }
  }, [user]);

  useEffect(() => { loadStats(); }, [loadStats]);

  const code = stats?.standReferralCode || '';
  const link = code ? buildReferralLink(code) : '';
  const credit = stats?.festinCredit || 0;
  const creditPct = Math.min(100, Math.round((credit / REFERRAL_CREDIT_CAP) * 100));

  const handleCopy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Lien copié !');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copie impossible — copiez le lien manuellement.');
    }
  };

  const handleShare = async () => {
    if (!link) return;
    const shareData = {
      title: 'Rejoins-moi sur Festin',
      text: `Inscris-toi sur Festin avec mon lien et reçois ${REFERRAL_WELCOME_DISCOUNT}€ de réduction sur ta première candidature 🎪`,
      url: link,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* annulé */ }
    } else {
      handleCopy();
    }
  };

  if (loading || fetching) {
    return (
      <div className="min-h-screen bg-[#0d0e12] text-white flex items-center justify-center">
        <div className="animate-pulse text-gray-500 text-sm font-semibold">Chargement du parrainage…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0e12] text-white font-sans pb-24 pt-14 md:pt-16">
      <div className="max-w-4xl mx-auto px-5 py-6">

        {/* Header */}
        <button
          onClick={() => navigate('/stand/profile')}
          className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors mb-6"
        >
          <ChevronLeft className="w-4 h-4" /> Retour au tableau de bord
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-2xl bg-[#FF5C00]/15 border border-[#FF5C00]/25 flex items-center justify-center">
            <Gift className="w-6 h-6 text-[#FF5C00]" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight">Parrainage exposant</h1>
            <p className="text-sm text-gray-500">Invitez d'autres exposants, cumulez du crédit Festin.</p>
          </div>
        </div>

        {/* Crédit + stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
          <div className="sm:col-span-1 bg-gradient-to-br from-[#FF5C00]/15 to-[#FF5C00]/5 border border-[#FF5C00]/25 rounded-2xl p-5 flex flex-col gap-1">
            <div className="flex items-center gap-2 text-[#FF5C00]">
              <Wallet className="w-4 h-4" />
              <span className="text-[11px] font-black uppercase tracking-wider">Crédit disponible</span>
            </div>
            <span className="text-3xl font-black text-white">{credit}€</span>
            <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden mt-1">
              <div className="h-full bg-[#FF5C00] rounded-full transition-all duration-700" style={{ width: `${creditPct}%` }} />
            </div>
            <span className="text-[10px] text-gray-500 font-medium mt-0.5">Plafond {REFERRAL_CREDIT_CAP}€</span>
          </div>

          <div className="bg-white/3 border border-white/8 rounded-2xl p-5 flex flex-col gap-1">
            <div className="flex items-center gap-2 text-gray-400">
              <Users className="w-4 h-4" />
              <span className="text-[11px] font-black uppercase tracking-wider">Filleuls</span>
            </div>
            <span className="text-3xl font-black text-white">{stats?.totalFilleuls || 0}</span>
            <span className="text-[10px] text-gray-500 font-medium">Exposants parrainés</span>
          </div>

          <div className="bg-emerald-500/5 border border-emerald-500/15 rounded-2xl p-5 flex flex-col gap-1">
            <div className="flex items-center gap-2 text-emerald-400">
              <TrendingUp className="w-4 h-4" />
              <span className="text-[11px] font-black uppercase tracking-wider">Confirmés</span>
            </div>
            <span className="text-3xl font-black text-emerald-400">{stats?.confirmedFilleuls || 0}</span>
            <span className="text-[10px] text-gray-500 font-medium">= {(stats?.confirmedFilleuls || 0) * REFERRAL_CREDIT_PER_FILLEUL}€ gagnés</span>
          </div>
        </div>

        {/* Lien de parrainage */}
        <div className="mt-6 bg-white/3 border border-white/8 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Link2 className="w-4 h-4 text-[#FF5C00]" />
            <span className="text-sm font-black uppercase tracking-wider text-white">Votre lien personnel</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 bg-black/40 border border-white/10 rounded-xl px-4 py-3 font-mono text-sm text-gray-300 truncate">
              {link || '—'}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCopy}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-white/8 hover:bg-white/15 border border-white/10 text-white text-sm font-bold rounded-xl transition-all active:scale-95"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copié' : 'Copier'}
              </button>
              <button
                onClick={handleShare}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-[#FF5C00] hover:bg-[#eb5500] text-black text-sm font-black rounded-xl transition-all active:scale-95 shadow-[0_0_20px_rgba(255,92,0,0.25)]"
              >
                <Share2 className="w-4 h-4" /> Partager
              </button>
            </div>
          </div>
          {code && (
            <p className="text-[11px] text-gray-500 mt-3">
              Code : <span className="font-mono font-bold text-gray-400">{code}</span>
            </p>
          )}
        </div>

        {/* Comment ça marche */}
        <div className="mt-6 bg-[#FF5C00]/5 border border-[#FF5C00]/15 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-[#FF5C00]" />
            <span className="text-sm font-black uppercase tracking-wider text-white">Comment ça marche</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { n: 1, t: 'Partagez votre lien', d: 'Envoyez-le à d\'autres exposants (food, artisans, créateurs).' },
              { n: 2, t: `Ils reçoivent ${REFERRAL_WELCOME_DISCOUNT}€`, d: `Réduction de bienvenue sur leur 1re candidature.` },
              { n: 3, t: `Vous gagnez ${REFERRAL_CREDIT_PER_FILLEUL}€`, d: `Crédité quand un filleul confirme (paiement). Cumulable jusqu'à ${REFERRAL_CREDIT_CAP}€.` },
            ].map((s) => (
              <div key={s.n} className="flex flex-col gap-1.5">
                <div className="w-8 h-8 rounded-lg bg-[#FF5C00]/15 border border-[#FF5C00]/20 flex items-center justify-center text-[#FF5C00] font-black text-sm">
                  {s.n}
                </div>
                <span className="text-sm font-bold text-white">{s.t}</span>
                <span className="text-xs text-gray-500 leading-relaxed">{s.d}</span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-gray-500 mt-4 leading-relaxed">
            Votre crédit est automatiquement déduit de vos prochains frais de service Festin. Il ne périme pas.
          </p>
        </div>

        {/* Liste des filleuls */}
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-black uppercase tracking-wider text-white">Mes filleuls</span>
          </div>
          {(!stats?.filleuls || stats.filleuls.length === 0) ? (
            <div className="bg-white/3 border border-white/8 border-dashed rounded-2xl p-8 text-center">
              <Gift className="w-8 h-8 text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-400 font-semibold">Aucun filleul pour l'instant</p>
              <p className="text-xs text-gray-600 mt-1">Partagez votre lien pour commencer à cumuler du crédit.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {stats.filleuls.map((f) => {
                const meta = STATUS_META[f.status] || STATUS_META.pending;
                const Icon = meta.icon;
                return (
                  <div key={f.applicationId} className="flex items-center justify-between gap-3 bg-white/3 border border-white/8 rounded-xl p-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                        <Users className="w-4 h-4 text-gray-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-white truncate">{f.standName || 'Exposant'}</p>
                        {f.paidAt && (
                          <p className="text-[11px] text-gray-500">Confirmé le {new Date(f.paidAt).toLocaleDateString('fr-FR')}</p>
                        )}
                      </div>
                    </div>
                    <div className={`shrink-0 flex items-center gap-1.5 text-[11px] font-black px-2.5 py-1.5 rounded-lg border ${meta.badge}`}>
                      <Icon className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">{meta.label}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
